import { Injectable } from "@angular/core";
import { SequenceStrategyModel } from "../model/sequence-strategy.model";

@Injectable()
export class LogicSequenceStrategyService {
    calcularMedia(item1: SequenceStrategyModel, item2: SequenceStrategyModel): number {
        const primeiroPesoMedioPonderado = item1.data.peso_medio_ponderado;
        const segundoPesoMedioPonderado = item2.data.peso_medio_ponderado;
        const primeiroTotalDefeitosTotaisPonderado = item1.data.total_defeitos_totais_ponderado;
        const segundoTotalDefeitosTotaisPonderado = item2.data.total_defeitos_totais_ponderado;
        const resultado = (Math.abs(parseFloat(segundoPesoMedioPonderado) - parseFloat(primeiroPesoMedioPonderado)) / 1.8) + (Math.abs(parseFloat(segundoTotalDefeitosTotaisPonderado) - parseFloat(primeiroTotalDefeitosTotaisPonderado)) / 4.5);
        return resultado;
    }

    arredondamentoExcel(valor: number, qtdCasaDecimal: number): number {
        return (Math.floor((Math.pow(10, qtdCasaDecimal)*valor)+0.5)*Math.pow(10, -qtdCasaDecimal))
    }

    montarCalculoMedia(lista: SequenceStrategyModel[]): number {
        let mediaTotal = 0;
        for (let i = 0; i < lista.length - 1; i++) {
            mediaTotal += this.calcularMedia(lista[i], lista[i + 1]);
        }
        return mediaTotal;
    }

    montarRegraSequenciaOtimizada(lista: SequenceStrategyModel[]): SequenceStrategyModel[] {
        let melhorSequenia = true;
        while (melhorSequenia) {
            melhorSequenia = false;
            for (let i = 0; i < lista.length - 1; i++) {
                for (let j = i + 1; j < lista.length; j++) {
                    const media = this.calcularMedia(lista[i], lista[j]);
                    if (media < 0) {
                        lista.splice(i, j - i + 1, ...lista.slice(i, j - i + 1).reverse());
                        melhorSequenia = true;
                    }
                }
            }
        }
        return lista;
    }

    montarSequenciaOtimizada(lista: SequenceStrategyModel[], tipoTabela: string): SequenceStrategyModel[] {
        let sequenciaFinal: SequenceStrategyModel[] = [], melhorSequenciaIncicial = Number.MAX_VALUE, melhorSequenciaFinal = Number.MAX_VALUE;

        if(tipoTabela === "nao-segregado") {
            const otimizacaoLotes = this.montarOtimizadorGruposNaoSegregados(lista);
            for (const ListaOtimizacaoLotes of otimizacaoLotes) {
                const initialSequence = this.montarRegraSequenciaOtimizada(ListaOtimizacaoLotes.slice());
                const mediaSequenciaInicial = this.montarCalculoMedia(initialSequence);
                if (mediaSequenciaInicial < melhorSequenciaIncicial) {
                    melhorSequenciaIncicial = mediaSequenciaInicial;
                    sequenciaFinal = initialSequence;
                }
            }
        }
        else if(tipoTabela === "segregado") {
            const otimizacaoLotesSegregado = this.montarOtimizadorGruposSegregados(lista);
            for (const ListaOtimizacaoContratos of otimizacaoLotesSegregado) {
                const endingSequence = this.montarRegraSequenciaOtimizada(ListaOtimizacaoContratos.slice());
                const mediaSequenciaFinal = this.montarCalculoMedia(endingSequence);
                if (mediaSequenciaFinal < melhorSequenciaFinal) {
                    melhorSequenciaFinal = mediaSequenciaFinal;
                    sequenciaFinal = endingSequence;
                }
            } 
        }

        return sequenciaFinal;
    }

    montarOtimizadorContratos<T extends SequenceStrategyModel>(lista: T[]): T[][] {
        const contratos = Array.from(this.agruparLotesPor("contrato", lista).values());
        const permutacaoGrupos = this.otimizarContratos(contratos);
        const resultado: T[][] = [];
        for (const permutacaoGrupo of permutacaoGrupos) {
            const permutacao: T[] = [];
            for (const contrato of permutacaoGrupo) {
                permutacao.push(...contrato);
            }
            resultado.push(permutacao);
        }
        return resultado;
    }

    agruparLotesPor<T extends SequenceStrategyModel>(chave: string, lista: T[]): Map<T[keyof T], T[]> {
        const mapa = new Map<T[keyof T], T[]>();
        for (const item of lista) {
            let valor;
            if(chave.toUpperCase() === "contrato".toUpperCase())
                valor = item.data.contrato_juridico;
            else if(chave.toUpperCase() === "faixa".toUpperCase())
                valor = item.grupo_faixa;
            if (mapa.has(valor)) {
                mapa.get(valor)!.push(item);
            } else {
                mapa.set(valor, [item]);
            }
        }
        return mapa;
    }

    montarOtimizadorGruposSegregados(lista: SequenceStrategyModel[]): SequenceStrategyModel[][] {
        const grupo = Array.from(this.agruparLotesPor("faixa", lista).values());
        const permutacoesGrupo: SequenceStrategyModel[][][] = [];
        
        for (const chave in grupo) {
            permutacoesGrupo.push(this.otimizarGrupos(grupo[chave]));
        }
        
        return this.combinarPermutacoesGrupoSegregado(permutacoesGrupo);
    }

    montarOtimizadorGruposNaoSegregados(lista: SequenceStrategyModel[]): SequenceStrategyModel[][] {
        const grupos = Array.from(this.agruparLotesPor("contrato", lista).values());
        const gruposOtimizados = grupos.map(grupo => this.otimizarGrupos(grupo));
        const combinacoesCompleta = this.combinarPermutacoesGrupoNaoSegregado(gruposOtimizados);
        
        return combinacoesCompleta;
    }
    

    combinarPermutacoesGrupoSegregado<T extends SequenceStrategyModel>(permutacoesAgrupadas: T[][][]): T[][] {
        const combine = (acumulado: T[][], atual: T[][]) => {
            const combinado: T[][] = [];
            acumulado.forEach(a => {
                atual.forEach(b => {
                    combinado.push([...a, ...b]);
                });
            });
            return combinado;
        };
        return permutacoesAgrupadas.reduce(combine, [[]]);
    }

    combinarPermutacoesGrupoNaoSegregado<T>(permutacoesAgrupadas: T[][][]): T[][] {
        return permutacoesAgrupadas.reduce<T[][]>((acumulado, atual) => {
            return acumulado.reduce<T[][]>((result, a) => {
                return result.concat(atual.map(b => [...a, ...b]));
            }, []);
        }, [[]]);
    }

    otimizarContratos<T>(lista: T[]): T[][] {
        const resultado: T[][] = [];
        const stack: { arr: T[], size: number }[] = [{ arr: [...lista], size: lista.length }];
    
        while (stack.length) {
            const { arr, size } = stack.pop()!;
            if (size === 1) {
                resultado.push([...arr]);
            } else {
                for (let i = 0; i < size; i++) {
                    stack.push({ arr: [...arr], size: size - 1 });
                    const j = size % 2 === 0 ? i : 0;
                    [arr[size - 1], arr[j]] = [arr[j], arr[size - 1]];
                }
            }
        }
    
        return resultado;
    }    

    otimizarGrupos<T>(lista: T[]): T[][] {
        const resultado: T[][] = [];
        const stack: { arr: T[], size: number }[] = [{ arr: [...lista], size: lista.length }];
    
        while (stack.length) {
            const { arr, size } = stack.pop()!;
            if (size === 1) {
                resultado.push([...arr]);
            } else {
                for (let i = 0; i < size; i++) {
                    stack.push({ arr: [...arr], size: size - 1 });
                    const j = size % 2 === 0 ? i : 0;
                    [arr[size - 1], arr[j]] = [arr[j], arr[size - 1]];
                }
            }
        }
    
        return resultado;
    }
}